import { appConfig } from "@/constants";
import type { SweepstakeInfo } from "@/domain/gems-game/domain/lib/Sweepstake";

import { EnvironmentNames } from "@/types";
import {
  Body,
  Box,
  Button,
  Checkbox,
  Heading,
  Select,
  Stack,
  TextInput,
} from "@biom3/react";
import { useCallback, useEffect, useState } from "react";
import { useSWRConfig } from "swr";
import {
  type WeeklyRewardsPeriod,
  useWeeklyRewards,
} from "./WeeklyRewardsProvider";
import type { EligibilityResponse } from "./types";

const PERIODS: WeeklyRewardsPeriod[] = [
  "REDEEM",
  "CALCULATE",
  "CLAIM",
  "THANKS",
  "IDLE",
];

// Define ticket types for better type safety
type TicketStatus = "claimable" | "claimed" | "locked" | "expired";

type TicketData = {
  status: TicketStatus;
  id: string;
  type: string;
  granted_at: string;
  expires_at: string;
  source: string;
  rarity: string;
  claimed_at?: string;
  metadata?: Array<{
    type: string;
    metadata: {
      amount: string;
      token_id?: string;
      is_jackpot?: boolean; // TODO: Deprecated, use reward_source instead
      reward_source?: "" | "jackpot" | "referral"; // "" = normal token, "jackpot" = mythic reward, "referral" = referral reward
    };
  }>;
};

// Define SweepstakeData type
type SweepstakeData = {
  current?: SweepstakeInfo;
  previous?: SweepstakeInfo;
};

export const WeeklyRewardsDevPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { mutate: globalMutate } = useSWRConfig();
  const [activeTab, setActiveTab] = useState<string>("periods");

  const {
    currentPeriod,
    sweepstake,
    sweepstakeTickets,
    eligibility,
    walletGems,
    setDevOverridePeriod,
  } = useWeeklyRewards();

  // Log each value from useWeeklyRewards in separate useEffects for debugging
  useEffect(() => {
    console.log("@@@@ currentPeriod", currentPeriod);
  }, [currentPeriod]);

  useEffect(() => {
    console.log("@@@@ sweepstake", sweepstake);
  }, [sweepstake]);

  useEffect(() => {
    console.log("@@@@ sweepstakeTickets", sweepstakeTickets);
  }, [sweepstakeTickets]);

  useEffect(() => {
    console.log("@@@@ eligibility", eligibility);
  }, [eligibility]);

  useEffect(() => {
    console.log("@@@@ walletGems", walletGems);
  }, [walletGems]);

  // Mock data states
  const [eligibilityData, setEligibilityData] = useState<EligibilityResponse>({
    is_eligible: true,
    rules: {
      have_linked_any_social_media: true,
      have_verified_phone: true,
      is_not_sybil: true,
      is_not_sanctioned: true,
      is_kyc_exempt: true,
      has_valuable_key: true,
    },
  });

  const defaultCurrentSweepstake: SweepstakeInfo = {
    participantsAmount: "1200",
    gemsEntryAmount: 500,
    startDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(),
    questId: 1,
    questName: "",
    questExternalID: "weekly-1",
    totalIMX: "10",
    totalIMXSansJackpot: "8",
    isLiveProgressKey: false,
    activityStartDate: new Date(
      Date.now() - 3 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    activityEndDate: new Date(
      Date.now() + 4 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    calculationPeriodEndDate: new Date(
      Date.now() + 3 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    trigger: "weekly-rewards-1",
    jackpots: [
      {
        winner_count: 1,
        imx_reward: "2",
      },
      {
        winner_count: 2,
        imx_reward: "1",
      },
      {
        winner_count: 3,
        imx_reward: "0.5",
      },
    ],
    jackpotWinners: {
      "2": [
        {
          walletAddress: "0x123456789",
          amount: "2",
          hasClaimed: true,
        },
      ],
      "1": [
        {
          walletAddress: "0x123456788",
          amount: "1",
          hasClaimed: true,
        },
      ],
      "0.5": [
        {
          walletAddress: "0x123456787",
          amount: "0.5",
          hasClaimed: true,
        },
      ],
    },
  };

  const defaultPreviousSweepstake: SweepstakeInfo = {
    participantsAmount: "980",
    gemsEntryAmount: 500,
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    questExternalID: "weekly-0",
    totalIMX: "10",
    totalIMXSansJackpot: "8",
    calculationPeriodEndDate: new Date(
      Date.now() + 3 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    questId: 0,
    questName: "",
    activityStartDate: new Date(
      Date.now() - 14 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    activityEndDate: new Date(
      Date.now() - 7 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    trigger: "weekly-rewards-0",
    isLiveProgressKey: false,
    jackpots: [
      {
        winner_count: 1,
        imx_reward: "2",
      },
      {
        winner_count: 2,
        imx_reward: "1",
      },
      {
        winner_count: 3,
        imx_reward: "0.5",
      },
    ],
    jackpotWinners: {
      "2": [
        {
          walletAddress: "0x123456789",
          amount: "2",
          hasClaimed: true,
        },
      ],
      "1": [
        {
          walletAddress: "0x123456788",
          amount: "1",
          hasClaimed: true,
        },
      ],
      "0.5": [
        {
          walletAddress: "0x123456787",
          amount: "0.5",
          hasClaimed: true,
        },
      ],
    },
  };

  const [sweepstakeData, setSweepstakeData] = useState<SweepstakeData>({
    current: defaultCurrentSweepstake,
    previous: defaultPreviousSweepstake,
  });

  const [ticketsData, setTicketsData] = useState<TicketData[]>([
    {
      status: "locked",
      id: "ticket-1",
      type: "sweepstake_ticket",
      granted_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      source: "weekly-rewards-1",
      rarity: "legendary",
    },
    {
      status: "claimable",
      id: "ticket-0",
      type: "sweepstake_ticket",
      granted_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      expires_at: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
      claimed_at: undefined,
      source: "weekly-rewards-0",
      rarity: "epic",
      metadata: [
        {
          type: "token",
          metadata: {
            amount: "100",
            token_id: "imx",
          },
        },
      ],
    },
  ]);

  const [walletGemsData, setWalletGemsData] = useState({
    gems: 1000,
    walletAddress: "0x123456789abcdef",
    dailyGemsClaimable: 50,
  });

  const [apiErrors, setApiErrors] = useState({
    eligibility: false,
    sweepstake: false,
    tickets: false,
    walletGems: false,
  });

  // Apply mock data to SWR cache
  const applyMockData = useCallback(() => {
    // Eligibility
    if (apiErrors.eligibility) {
      globalMutate(
        (key) =>
          Array.isArray(key) && key[0] === "/v2/rewards/redemption/eligibility",
        () => {
          return new Error("Simulated API error for eligibility");
        },
        { populateCache: true, revalidate: false },
      );
    } else {
      globalMutate(
        (key) =>
          Array.isArray(key) && key[0] === "/v2/rewards/redemption/eligibility",
        eligibilityData,
        { populateCache: true, revalidate: false },
      );
    }

    // Sweepstake
    if (apiErrors.sweepstake) {
      globalMutate(
        "/v1/rewards/perpetual-rewards",
        () => {
          return new Error("Simulated API error for sweepstake");
        },
        { populateCache: true, revalidate: false },
      );
    } else {
      globalMutate("/v1/rewards/perpetual-rewards", sweepstakeData, {
        populateCache: true,
        revalidate: false,
      });
    }

    // Tickets
    if (apiErrors.tickets) {
      globalMutate(
        (key) =>
          Array.isArray(key) &&
          key[0] === "/v2/rewards/earnings?reward_type=sweepstake_ticket",
        () => {
          return new Error("Simulated API error for tickets");
        },
        { populateCache: true, revalidate: false },
      );
    } else {
      globalMutate(
        (key) =>
          Array.isArray(key) &&
          key[0] === "/v2/rewards/earnings?reward_type=sweepstake_ticket",
        ticketsData,
        { populateCache: true, revalidate: false },
      );
    }

    // Wallet Gems
    if (apiErrors.walletGems) {
      globalMutate(
        (key) => typeof key === "string" && key.includes("wallet/gems/"),
        () => {
          return new Error("Simulated API error for wallet gems");
        },
        { populateCache: true, revalidate: false },
      );
    } else {
      globalMutate(
        (key) => typeof key === "string" && key.includes("wallet/gems/"),
        walletGemsData,
        { populateCache: true, revalidate: false },
      );
    }
  }, [
    apiErrors,
    eligibilityData,
    globalMutate,
    sweepstakeData,
    ticketsData,
    walletGemsData,
  ]);

  // Apply mock data when panel is expanded
  useEffect(() => {
    if (isExpanded) {
      applyMockData();
    }
  }, [isExpanded, applyMockData]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Use keyboard shortcut Ctrl+Shift+W to toggle panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "W") {
        e.preventDefault();
        toggleExpanded();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [toggleExpanded]);

  // Update eligibility rule
  const updateEligibilityRule = (
    rule: keyof EligibilityResponse["rules"],
    value: boolean,
  ) => {
    setEligibilityData((prev) => ({
      ...prev,
      rules: {
        ...prev.rules,
        [rule]: value,
      },
    }));
  };

  // Update eligibility is_eligible
  const updateEligibilityIsEligible = (value: boolean) => {
    setEligibilityData((prev) => ({
      ...prev,
      is_eligible: value,
    }));
  };

  // Update wallet gems
  const updateWalletGems = (gems: number) => {
    setWalletGemsData((prev) => ({
      ...prev,
      gems,
    }));
  };

  // Update ticket status
  const updateTicketStatus = (ticketId: string, status: TicketStatus) => {
    setTicketsData((prev) =>
      prev.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              status,
              claimed_at:
                status === "claimed"
                  ? new Date().toISOString()
                  : ticket.claimed_at,
            }
          : ticket,
      ),
    );
  };

  const toggleCurrentSweepstake = (hasCurrentSweepstake: boolean) => {
    setSweepstakeData((prev) => ({
      ...prev,
      current: hasCurrentSweepstake ? defaultCurrentSweepstake : undefined,
    }));
  };

  const togglePreviousSweepstake = (hasPreviousSweepstake: boolean) => {
    setSweepstakeData((prev) => ({
      ...prev,
      previous: hasPreviousSweepstake ? defaultPreviousSweepstake : undefined,
    }));
  };

  const updateSweepstakeTrigger = (
    type: "current" | "previous",
    trigger: string,
  ) => {
    setSweepstakeData((prev) => {
      if (type === "current" && prev.current) {
        return {
          ...prev,
          current: {
            ...prev.current,
            trigger,
          },
        };
      }
      if (type === "previous" && prev.previous) {
        return {
          ...prev,
          previous: {
            ...prev.previous,
            trigger,
          },
        };
      }
      return prev;
    });
  };

  // Setup data for a specific period
  const setupPeriodData = (period: WeeklyRewardsPeriod) => {
    // First, call the period change function
    setDevOverridePeriod(period);

    // Now set up the data state corresponding to the period
    switch (period) {
      case "REDEEM":
        // REDEEM: Current sweepstake active, user not participating, eligible
        setSweepstakeData({
          current: {
            ...defaultCurrentSweepstake,
            startDate: new Date(
              Date.now() - 2 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            endDate: new Date(
              Date.now() + 5 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
          previous: undefined,
        });

        // No ticket for current sweepstake
        setTicketsData([]);

        // User is eligible
        setEligibilityData({
          is_eligible: true,
          rules: {
            have_linked_any_social_media: true,
            have_verified_phone: true,
            is_not_sybil: true,
            is_not_sanctioned: true,
            is_kyc_exempt: true,
            has_valuable_key: false,
          },
        });
        break;

      case "CALCULATE":
        // CALCULATE: User participated but rewards not ready yet
        setSweepstakeData({
          current: {
            ...defaultCurrentSweepstake,
            startDate: new Date(
              Date.now() - 5 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            endDate: new Date(
              Date.now() + 2 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
          previous: undefined,
        });

        // User has locked ticket
        setTicketsData([
          {
            status: "locked",
            id: "ticket-1",
            type: "sweepstake_ticket",
            granted_at: new Date(
              Date.now() - 3 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            expires_at: new Date(
              Date.now() + 30 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            source: "weekly-rewards-1", // matches current trigger
            rarity: "legendary",
          },
        ]);
        break;

      case "CLAIM":
        // CLAIM: Previous sweepstake done, user has claimable ticket
        setSweepstakeData({
          current: undefined,
          previous: {
            ...defaultPreviousSweepstake,
            startDate: new Date(
              Date.now() - 14 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            endDate: new Date(
              Date.now() - 7 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        });

        // User has claimable ticket from previous
        setTicketsData([
          {
            status: "claimable",
            id: "ticket-0",
            type: "sweepstake_ticket",
            granted_at: new Date(
              Date.now() - 10 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            expires_at: new Date(
              Date.now() + 20 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            source: "weekly-rewards-0", // matches previous trigger
            rarity: "epic",
            metadata: [
              {
                type: "token",
                metadata: {
                  amount: "100",
                  token_id: "imx",
                },
              },
            ],
          },
        ]);
        break;

      case "THANKS":
        // THANKS: Previous sweepstake done, user has claimed their prize
        setSweepstakeData({
          current: undefined,
          previous: {
            ...defaultPreviousSweepstake,
            startDate: new Date(
              Date.now() - 14 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            endDate: new Date(
              Date.now() - 7 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          },
        });

        // User has claimed ticket
        setTicketsData([
          {
            status: "claimed",
            id: "ticket-0",
            type: "sweepstake_ticket",
            granted_at: new Date(
              Date.now() - 10 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            expires_at: new Date(
              Date.now() + 20 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            claimed_at: new Date(
              Date.now() - 1 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            source: "weekly-rewards-0", // matches previous trigger
            rarity: "epic",
            metadata: [
              {
                type: "token",
                metadata: {
                  amount: "100",
                  token_id: "imx",
                },
              },
            ],
          },
        ]);
        break;

      case "IDLE":
        // IDLE: No active sweepstakes, ineligible
        setSweepstakeData({
          current: undefined,
          previous: undefined,
        });

        setTicketsData([]);

        // User is not eligible
        setEligibilityData({
          is_eligible: false,
          rules: {
            have_linked_any_social_media: false,
            have_verified_phone: false,
            is_not_sybil: true,
            is_not_sanctioned: true,
            is_kyc_exempt: true,
            has_valuable_key: false,
          },
        });
        break;
    }

    // Apply the changes immediately
    setTimeout(() => {
      applyMockData();
    }, 100);
  };

  if (appConfig.ENVIRONMENT !== EnvironmentNames.DEV) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: "0",
        right: "0",
        p: "base.spacing.x2",
        bg: "base.color.background.surface.primary",
        borderTopLeftRadius: "base.borderRadius.medium",
        boxShadow: "base.shadow.medium",
        border: "1px solid",
        background: "black",
        borderColor: "base.color.border.secondary",
        width: "100vw",
        maxWidth: "100%",
        height: isExpanded ? "30vh" : "auto",
        transition: "all 0.2s ease-in-out",
        overflowY: isExpanded ? "auto" : "hidden",
        zIndex: "9999",
      }}
    >
      <Stack gap="base.spacing.x2">
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Stack direction="row">
            <Button
              size="small"
              variant="tertiary"
              onClick={toggleExpanded}
              sx={{ minWidth: "0", px: "base.spacing.x1" }}
            >
              <Button.Icon
                icon={isExpanded ? "ChevronCollapse" : "ChevronExpand"}
              />
            </Button>
            <Body size="small" weight="bold">
              Weekly Rewards Dev Panel
            </Body>
          </Stack>
          {isExpanded && (
            <Button
              size="small"
              variant="primary"
              onClick={applyMockData}
              sx={{ px: "base.spacing.x2" }}
            >
              Apply Changes
            </Button>
          )}
        </Stack>

        {isExpanded && (
          <Stack gap="base.spacing.x4">
            {/* Manual Tab Implementation since biom3/react doesn't expose Tabs component */}
            <Stack gap="base.spacing.x3">
              <Stack direction="row" gap="base.spacing.x2">
                {[
                  "periods",
                  "eligibility",
                  "sweepstake",
                  "tickets",
                  "gems",
                  "errors",
                ].map((tab) => (
                  <Button
                    key={tab}
                    size="small"
                    variant={activeTab === tab ? "primary" : "secondary"}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </Button>
                ))}
              </Stack>

              {activeTab === "periods" && (
                <Stack gap="base.spacing.x2">
                  <Heading size="xSmall">Period Control</Heading>
                  <Box
                    sx={{
                      p: "base.spacing.x2",
                      bg: "base.color.background.surface.secondary",
                      borderRadius: "base.borderRadius.medium",
                    }}
                  >
                    <Body size="small">Current Period: {currentPeriod}</Body>
                  </Box>
                  <Body size="small">
                    Select a period to automatically set up all relevant data
                    for that period.
                  </Body>
                  <Stack gap="base.spacing.x2">
                    {PERIODS.map((period) => (
                      <Button
                        key={period}
                        size="small"
                        variant={
                          currentPeriod === period ? "primary" : "secondary"
                        }
                        onClick={() => setupPeriodData(period)}
                        sx={{ justifyContent: "flex-start" }}
                      >
                        {period}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              )}

              {activeTab === "eligibility" && (
                <Stack gap="base.spacing.x3">
                  <Heading size="xSmall">Eligibility API Control</Heading>
                  <Stack
                    direction="row"
                    gap="base.spacing.x2"
                    sx={{ alignItems: "center" }}
                  >
                    <Checkbox
                      checked={eligibilityData.is_eligible}
                      onChange={(e) =>
                        updateEligibilityIsEligible(e.target.checked)
                      }
                    />
                    <Body size="small">is_eligible</Body>
                  </Stack>

                  <Heading size="xSmall">Eligibility Rules</Heading>
                  <Stack gap="base.spacing.x2">
                    {Object.entries(eligibilityData.rules).map(
                      ([key, value]) => (
                        <Stack
                          key={key}
                          direction="row"
                          gap="base.spacing.x2"
                          sx={{ alignItems: "center" }}
                        >
                          <Checkbox
                            checked={value}
                            onChange={(e) =>
                              updateEligibilityRule(
                                key as keyof EligibilityResponse["rules"],
                                e.target.checked,
                              )
                            }
                          />
                          <Body size="small">{key}</Body>
                        </Stack>
                      ),
                    )}
                  </Stack>
                </Stack>
              )}

              {activeTab === "sweepstake" && (
                <Stack gap="base.spacing.x3">
                  <Heading size="xSmall">Sweepstake Mock Data</Heading>
                  <Stack gap="base.spacing.x2">
                    <Body size="small" weight="bold">
                      Current Sweepstake
                    </Body>
                    <Stack
                      direction="row"
                      gap="base.spacing.x2"
                      sx={{ alignItems: "center" }}
                    >
                      <Checkbox
                        checked={!!sweepstakeData.current}
                        onChange={(e) =>
                          toggleCurrentSweepstake(e.target.checked)
                        }
                      />
                      <Body size="small">Has current sweepstake</Body>
                    </Stack>

                    {sweepstakeData.current && (
                      <Stack gap="base.spacing.x2">
                        <TextInput
                          value={sweepstakeData.current.trigger}
                          onChange={(e) =>
                            updateSweepstakeTrigger("current", e.target.value)
                          }
                          placeholder="Trigger ID"
                          sizeVariant="medium"
                        >
                          <TextInput.Button>Trigger ID</TextInput.Button>
                        </TextInput>

                        <TextInput
                          value={sweepstakeData.current.participantsAmount}
                          onChange={(e) => {
                            setSweepstakeData((prev) => ({
                              ...prev,
                              current: prev.current
                                ? {
                                    ...prev.current,
                                    participantsAmount: e.target.value,
                                  }
                                : undefined,
                            }));
                          }}
                          placeholder="Participants"
                          sizeVariant="medium"
                        >
                          <TextInput.Button>Participants</TextInput.Button>
                        </TextInput>

                        <TextInput
                          value={sweepstakeData.current.gemsEntryAmount}
                          type="number"
                          onChange={(e) => {
                            setSweepstakeData((prev) => ({
                              ...prev,
                              current: prev.current
                                ? {
                                    ...prev.current,
                                    gemsEntryAmount:
                                      Number.parseInt(e.target.value) || 0,
                                  }
                                : undefined,
                            }));
                          }}
                          placeholder="Gems Entry Amount"
                          sizeVariant="medium"
                        >
                          <TextInput.Button>Gems Entry</TextInput.Button>
                        </TextInput>

                        <Stack direction="row" gap="base.spacing.x2">
                          <TextInput
                            value={
                              new Date(sweepstakeData.current.startDate)
                                .toISOString()
                                .split("T")[0]
                            }
                            type="date"
                            onChange={(e) => {
                              const date = new Date(e.target.value);
                              if (!Number.isNaN(date.getTime())) {
                                setSweepstakeData((prev) => ({
                                  ...prev,
                                  current: prev.current
                                    ? {
                                        ...prev.current,
                                        startDate: date.toISOString(),
                                      }
                                    : undefined,
                                }));
                              }
                            }}
                            sizeVariant="medium"
                          >
                            <TextInput.Button>Start Date</TextInput.Button>
                          </TextInput>

                          <TextInput
                            value={
                              new Date(sweepstakeData.current.endDate)
                                .toISOString()
                                .split("T")[0]
                            }
                            type="date"
                            onChange={(e) => {
                              const date = new Date(e.target.value);
                              if (!Number.isNaN(date.getTime())) {
                                setSweepstakeData((prev) => ({
                                  ...prev,
                                  current: prev.current
                                    ? {
                                        ...prev.current,
                                        endDate: date.toISOString(),
                                      }
                                    : undefined,
                                }));
                              }
                            }}
                            sizeVariant="medium"
                          >
                            <TextInput.Button>End Date</TextInput.Button>
                          </TextInput>
                        </Stack>
                      </Stack>
                    )}

                    <Body size="small" weight="bold">
                      Previous Sweepstake
                    </Body>
                    <Stack
                      direction="row"
                      gap="base.spacing.x2"
                      sx={{ alignItems: "center" }}
                    >
                      <Checkbox
                        checked={!!sweepstakeData.previous}
                        onChange={(e) =>
                          togglePreviousSweepstake(e.target.checked)
                        }
                      />
                      <Body size="small">Has previous sweepstake</Body>
                    </Stack>

                    {sweepstakeData.previous && (
                      <Stack gap="base.spacing.x2">
                        <TextInput
                          value={sweepstakeData.previous.trigger}
                          onChange={(e) =>
                            updateSweepstakeTrigger("previous", e.target.value)
                          }
                          placeholder="Trigger ID"
                          sizeVariant="medium"
                        >
                          <TextInput.Button>Trigger ID</TextInput.Button>
                        </TextInput>

                        <TextInput
                          value={sweepstakeData.previous.participantsAmount}
                          onChange={(e) => {
                            setSweepstakeData((prev) => ({
                              ...prev,
                              previous: prev.previous
                                ? {
                                    ...prev.previous,
                                    participantsAmount: e.target.value,
                                  }
                                : undefined,
                            }));
                          }}
                          placeholder="Participants"
                          sizeVariant="medium"
                        >
                          <TextInput.Button>Participants</TextInput.Button>
                        </TextInput>

                        <TextInput
                          value={sweepstakeData.previous.gemsEntryAmount}
                          type="number"
                          onChange={(e) => {
                            setSweepstakeData((prev) => ({
                              ...prev,
                              previous: prev.previous
                                ? {
                                    ...prev.previous,
                                    gemsEntryAmount:
                                      Number.parseInt(e.target.value) || 0,
                                  }
                                : undefined,
                            }));
                          }}
                          placeholder="Gems Entry Amount"
                          sizeVariant="medium"
                        >
                          <TextInput.Button>Gems Entry</TextInput.Button>
                        </TextInput>

                        <Stack direction="row" gap="base.spacing.x2">
                          <TextInput
                            value={
                              new Date(sweepstakeData.previous.startDate)
                                .toISOString()
                                .split("T")[0]
                            }
                            type="date"
                            onChange={(e) => {
                              const date = new Date(e.target.value);
                              if (!Number.isNaN(date.getTime())) {
                                setSweepstakeData((prev) => ({
                                  ...prev,
                                  previous: prev.previous
                                    ? {
                                        ...prev.previous,
                                        startDate: date.toISOString(),
                                      }
                                    : undefined,
                                }));
                              }
                            }}
                            sizeVariant="medium"
                          >
                            <TextInput.Button>Start Date</TextInput.Button>
                          </TextInput>

                          <TextInput
                            value={
                              new Date(sweepstakeData.previous.endDate)
                                .toISOString()
                                .split("T")[0]
                            }
                            type="date"
                            onChange={(e) => {
                              const date = new Date(e.target.value);
                              if (!Number.isNaN(date.getTime())) {
                                setSweepstakeData((prev) => ({
                                  ...prev,
                                  previous: prev.previous
                                    ? {
                                        ...prev.previous,
                                        endDate: date.toISOString(),
                                      }
                                    : undefined,
                                }));
                              }
                            }}
                            sizeVariant="medium"
                          >
                            <TextInput.Button>End Date</TextInput.Button>
                          </TextInput>
                        </Stack>

                        <Heading size="xSmall">Jackpot Winners</Heading>
                        {Object.entries(
                          sweepstakeData.previous.jackpotWinners,
                        ).map(([prizeAmount, winners]) =>
                          winners.map((winner, winnerIdx) => {
                            const winnerId = `winner-${prizeAmount}-${winnerIdx}`;
                            return (
                              <Stack
                                key={winnerId}
                                gap="base.spacing.x1"
                                sx={{
                                  bg: "base.color.neutral.700",
                                  p: "base.spacing.x2",
                                  borderRadius: "base.borderRadius.medium",
                                }}
                              >
                                <Body size="small" weight="bold">
                                  Prize Amount: {prizeAmount}
                                </Body>
                                <TextInput
                                  value={winner.walletAddress}
                                  onChange={(e) => {
                                    if (sweepstakeData.previous) {
                                      const updatedWinners = {
                                        ...sweepstakeData.previous
                                          .jackpotWinners,
                                      };

                                      const updatedPrizeWinners = [
                                        ...updatedWinners[prizeAmount],
                                      ];
                                      updatedPrizeWinners[winnerIdx] = {
                                        ...updatedPrizeWinners[winnerIdx],
                                        walletAddress: e.target.value,
                                      };

                                      updatedWinners[prizeAmount] =
                                        updatedPrizeWinners;

                                      setSweepstakeData((prev) => ({
                                        ...prev,
                                        previous: prev.previous
                                          ? {
                                              ...prev.previous,
                                              jackpotWinners: updatedWinners,
                                            }
                                          : undefined,
                                      }));
                                    }
                                  }}
                                  placeholder="Wallet Address"
                                  sizeVariant="medium"
                                >
                                  <TextInput.Button>
                                    Wallet Address
                                  </TextInput.Button>
                                </TextInput>

                                <TextInput
                                  value={winner.amount}
                                  onChange={(e) => {
                                    if (sweepstakeData.previous) {
                                      const updatedWinners = {
                                        ...sweepstakeData.previous
                                          .jackpotWinners,
                                      };

                                      const updatedPrizeWinners = [
                                        ...updatedWinners[prizeAmount],
                                      ];
                                      updatedPrizeWinners[winnerIdx] = {
                                        ...updatedPrizeWinners[winnerIdx],
                                        amount: e.target.value,
                                      };

                                      updatedWinners[prizeAmount] =
                                        updatedPrizeWinners;

                                      setSweepstakeData((prev) => ({
                                        ...prev,
                                        previous: prev.previous
                                          ? {
                                              ...prev.previous,
                                              jackpotWinners: updatedWinners,
                                            }
                                          : undefined,
                                      }));
                                    }
                                  }}
                                  placeholder="Amount"
                                  sizeVariant="medium"
                                >
                                  <TextInput.Button>Amount</TextInput.Button>
                                </TextInput>

                                <Stack
                                  direction="row"
                                  gap="base.spacing.x2"
                                  sx={{ alignItems: "center" }}
                                >
                                  <Checkbox
                                    checked={winner.hasClaimed}
                                    onChange={(e) => {
                                      if (sweepstakeData.previous) {
                                        const updatedWinners = {
                                          ...sweepstakeData.previous
                                            .jackpotWinners,
                                        };

                                        const updatedPrizeWinners = [
                                          ...updatedWinners[prizeAmount],
                                        ];
                                        updatedPrizeWinners[winnerIdx] = {
                                          ...updatedPrizeWinners[winnerIdx],
                                          hasClaimed: e.target.checked,
                                        };

                                        updatedWinners[prizeAmount] =
                                          updatedPrizeWinners;

                                        setSweepstakeData((prev) => ({
                                          ...prev,
                                          previous: prev.previous
                                            ? {
                                                ...prev.previous,
                                                jackpotWinners: updatedWinners,
                                              }
                                            : undefined,
                                        }));
                                      }
                                    }}
                                  />
                                  <Body size="small">Has Claimed</Body>
                                </Stack>
                              </Stack>
                            );
                          }),
                        )}

                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            size="small"
                            onClick={() => {
                              if (sweepstakeData.previous) {
                                // Create a new prize amount group or add to existing
                                const newPrizeAmount = "100";
                                const updatedWinners = {
                                  ...sweepstakeData.previous.jackpotWinners,
                                };

                                if (!updatedWinners[newPrizeAmount]) {
                                  updatedWinners[newPrizeAmount] = [];
                                }

                                updatedWinners[newPrizeAmount] = [
                                  ...updatedWinners[newPrizeAmount],
                                  {
                                    walletAddress: "0x0000",
                                    amount: newPrizeAmount,
                                    hasClaimed: false,
                                  },
                                ];

                                setSweepstakeData((prev) => ({
                                  ...prev,
                                  previous: prev.previous
                                    ? {
                                        ...prev.previous,
                                        jackpotWinners: updatedWinners,
                                      }
                                    : undefined,
                                }));
                              }
                            }}
                          >
                            Add Winner
                          </Button>
                        </Box>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              )}

              {activeTab === "tickets" && (
                <Stack gap="base.spacing.x3">
                  <Heading size="xSmall">Tickets Mock Data</Heading>

                  {ticketsData.length === 0 && (
                    <Body size="small">
                      No tickets currently exist. Add tickets below.
                    </Body>
                  )}

                  {ticketsData.map((ticket, index) => (
                    <Stack
                      key={ticket.id}
                      gap="base.spacing.x2"
                      sx={{
                        p: "base.spacing.x2",
                        bg: "base.color.background.surface.secondary",
                        borderRadius: "base.borderRadius.medium",
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{ justifyContent: "space-between" }}
                      >
                        <Body size="small" weight="bold">
                          Ticket {index + 1}: {ticket.id}
                        </Body>
                        <Button
                          size="small"
                          variant="tertiary"
                          onClick={() => {
                            setTicketsData((prev) =>
                              prev.filter((t) => t.id !== ticket.id),
                            );
                          }}
                        >
                          <Button.Icon icon="Delete" />
                        </Button>
                      </Stack>

                      <TextInput
                        value={ticket.source}
                        onChange={(e) => {
                          setTicketsData((prev) =>
                            prev.map((t) =>
                              t.id === ticket.id
                                ? { ...t, source: e.target.value }
                                : t,
                            ),
                          );
                        }}
                        placeholder="Source (sweepstake trigger)"
                        sizeVariant="medium"
                      >
                        <TextInput.Button>Source</TextInput.Button>
                      </TextInput>

                      <TextInput
                        value={ticket.rarity}
                        onChange={(e) => {
                          setTicketsData((prev) =>
                            prev.map((t) =>
                              t.id === ticket.id
                                ? { ...t, rarity: e.target.value }
                                : t,
                            ),
                          );
                        }}
                        placeholder="Rarity (epic, legendary, etc.)"
                        sizeVariant="medium"
                      >
                        <TextInput.Button>Rarity</TextInput.Button>
                      </TextInput>

                      <Stack
                        direction="row"
                        gap="base.spacing.x2"
                        sx={{ alignItems: "center" }}
                      >
                        <Select
                          selectedOption={ticket.status}
                          onSelectChange={(value: unknown) =>
                            updateTicketStatus(ticket.id, value as TicketStatus)
                          }
                        >
                          <Select.Option optionKey="locked" value="locked">
                            locked
                          </Select.Option>
                          <Select.Option
                            optionKey="claimable"
                            value="claimable"
                          >
                            claimable
                          </Select.Option>
                          <Select.Option optionKey="claimed" value="claimed">
                            claimed
                          </Select.Option>
                          <Select.Option optionKey="expired" value="expired">
                            expired
                          </Select.Option>
                        </Select>
                        <Body size="small">Status</Body>
                      </Stack>

                      {ticket.status === "claimed" && ticket.claimed_at && (
                        <Body size="small">
                          Claimed at:{" "}
                          {new Date(ticket.claimed_at).toLocaleString()}
                        </Body>
                      )}

                      {ticket.metadata && ticket.metadata.length > 0 && (
                        <Stack gap="base.spacing.x1">
                          <Body size="small" weight="bold">
                            Metadata:
                          </Body>
                          {ticket.metadata.map((meta, metaIdx) => (
                            <Stack
                              key={`${ticket.id}-meta-${meta.type}-${metaIdx}`}
                              direction="row"
                              gap="base.spacing.x2"
                              sx={{ alignItems: "center" }}
                            >
                              <Select
                                selectedOption={meta.type}
                                onSelectChange={(value: unknown) => {
                                  setTicketsData((prev) => {
                                    const updatedTicket = prev.find(
                                      (t) => t.id === ticket.id,
                                    );
                                    if (!updatedTicket?.metadata) return prev;

                                    const updatedMetadata = [
                                      ...updatedTicket.metadata,
                                    ];
                                    updatedMetadata[metaIdx] = {
                                      ...updatedMetadata[metaIdx],
                                      type: value as string,
                                    };

                                    return prev.map((t) =>
                                      t.id === ticket.id
                                        ? {
                                            ...updatedTicket,
                                            metadata: updatedMetadata,
                                          }
                                        : t,
                                    );
                                  });
                                }}
                              >
                                <Select.Option optionKey="token" value="token">
                                  token
                                </Select.Option>
                                <Select.Option optionKey="point" value="point">
                                  point (gems)
                                </Select.Option>
                                <Select.Option
                                  optionKey="sweepstake_free_entry"
                                  value="sweepstake_free_entry"
                                >
                                  free entry
                                </Select.Option>
                                <Select.Option
                                  optionKey="sweepstake_ticket_upgrade"
                                  value="sweepstake_ticket_upgrade"
                                >
                                  ticket upgrade
                                </Select.Option>
                              </Select>

                              <TextInput
                                value={meta.metadata.amount}
                                onChange={(e) => {
                                  setTicketsData((prev) => {
                                    const updatedTicket = prev.find(
                                      (t) => t.id === ticket.id,
                                    );
                                    if (!updatedTicket?.metadata) return prev;

                                    const updatedMetadata = [
                                      ...updatedTicket.metadata,
                                    ];
                                    updatedMetadata[metaIdx] = {
                                      ...updatedMetadata[metaIdx],
                                      metadata: {
                                        ...updatedMetadata[metaIdx].metadata,
                                        amount: e.target.value,
                                      },
                                    };

                                    return prev.map((t) =>
                                      t.id === ticket.id
                                        ? {
                                            ...updatedTicket,
                                            metadata: updatedMetadata,
                                          }
                                        : t,
                                    );
                                  });
                                }}
                                placeholder="Amount"
                                sizeVariant="medium"
                              />

                              {meta.type === "token" && (
                                <>
                                  <TextInput
                                    value={meta.metadata.token_id || ""}
                                    onChange={(e) => {
                                      setTicketsData((prev) => {
                                        const updatedTicket = prev.find(
                                          (t) => t.id === ticket.id,
                                        );
                                        if (!updatedTicket?.metadata)
                                          return prev;

                                        const updatedMetadata = [
                                          ...updatedTicket.metadata,
                                        ];
                                        updatedMetadata[metaIdx] = {
                                          ...updatedMetadata[metaIdx],
                                          metadata: {
                                            ...updatedMetadata[metaIdx]
                                              .metadata,
                                            token_id: e.target.value,
                                          },
                                        };

                                        return prev.map((t) =>
                                          t.id === ticket.id
                                            ? {
                                                ...updatedTicket,
                                                metadata: updatedMetadata,
                                              }
                                            : t,
                                        );
                                      });
                                    }}
                                    placeholder="Token ID"
                                    sizeVariant="medium"
                                  />

                                  <Stack
                                    direction="row"
                                    gap="base.spacing.x1"
                                    sx={{ alignItems: "center" }}
                                  >
                                    <Select
                                      selectedOption={
                                        meta.metadata.reward_source ||
                                        (meta.metadata.is_jackpot
                                          ? "jackpot"
                                          : "")
                                      }
                                      onSelectChange={(value: unknown) => {
                                        setTicketsData((prev) => {
                                          const updatedTicket = prev.find(
                                            (t) => t.id === ticket.id,
                                          );
                                          if (!updatedTicket?.metadata)
                                            return prev;

                                          const updatedMetadata = [
                                            ...updatedTicket.metadata,
                                          ];
                                          updatedMetadata[metaIdx] = {
                                            ...updatedMetadata[metaIdx],
                                            metadata: {
                                              ...updatedMetadata[metaIdx]
                                                .metadata,
                                              reward_source:
                                                (value as
                                                  | ""
                                                  | "jackpot"
                                                  | "referral") || undefined,
                                              // Keep is_jackpot for backward compatibility
                                              is_jackpot: value === "jackpot",
                                            },
                                          };

                                          return prev.map((t) =>
                                            t.id === ticket.id
                                              ? {
                                                  ...updatedTicket,
                                                  metadata: updatedMetadata,
                                                }
                                              : t,
                                          );
                                        });
                                      }}
                                    >
                                      <Select.Option optionKey="" value="">
                                        Standard
                                      </Select.Option>
                                      <Select.Option
                                        optionKey="jackpot"
                                        value="jackpot"
                                      >
                                        Jackpot
                                      </Select.Option>
                                      <Select.Option
                                        optionKey="referral"
                                        value="referral"
                                      >
                                        Referral
                                      </Select.Option>
                                    </Select>
                                    <Body size="small">Reward Source</Body>
                                  </Stack>
                                </>
                              )}

                              <Button
                                size="small"
                                variant="tertiary"
                                onClick={() => {
                                  setTicketsData((prev) => {
                                    const updatedTicket = prev.find(
                                      (t) => t.id === ticket.id,
                                    );
                                    if (!updatedTicket?.metadata) return prev;

                                    const updatedMetadata =
                                      updatedTicket.metadata.filter(
                                        (_, idx) => idx !== metaIdx,
                                      );

                                    return prev.map((t) =>
                                      t.id === ticket.id
                                        ? {
                                            ...updatedTicket,
                                            metadata:
                                              updatedMetadata.length > 0
                                                ? updatedMetadata
                                                : undefined,
                                          }
                                        : t,
                                    );
                                  });
                                }}
                              >
                                <Button.Icon icon="Delete" />
                              </Button>
                            </Stack>
                          ))}

                          <Button
                            size="small"
                            variant="secondary"
                            onClick={() => {
                              setTicketsData((prev) => {
                                const updatedTicket = prev.find(
                                  (t) => t.id === ticket.id,
                                );
                                if (!updatedTicket) return prev;

                                const newMetadata = {
                                  type: "token",
                                  metadata: {
                                    amount: "100",
                                    token_id: "imx",
                                  },
                                };

                                const metadata = updatedTicket.metadata
                                  ? [...updatedTicket.metadata, newMetadata]
                                  : [newMetadata];

                                return prev.map((t) =>
                                  t.id === ticket.id
                                    ? { ...updatedTicket, metadata }
                                    : t,
                                );
                              });
                            }}
                          >
                            Add Prize
                          </Button>
                        </Stack>
                      )}

                      {(ticket.status === "claimable" ||
                        ticket.status === "claimed") &&
                        !ticket.metadata && (
                          <Stack direction="row" gap="base.spacing.x2">
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => {
                                setTicketsData((prev) =>
                                  prev.map((t) =>
                                    t.id === ticket.id
                                      ? {
                                          ...t,
                                          metadata: [
                                            {
                                              type: "token",
                                              metadata: {
                                                amount: "100",
                                                token_id: "imx",
                                              },
                                            },
                                          ],
                                        }
                                      : t,
                                  ),
                                );
                              }}
                            >
                              Add IMX Token
                            </Button>

                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => {
                                setTicketsData((prev) =>
                                  prev.map((t) =>
                                    t.id === ticket.id
                                      ? {
                                          ...t,
                                          metadata: [
                                            {
                                              type: "point",
                                              metadata: {
                                                amount: "500",
                                              },
                                            },
                                          ],
                                        }
                                      : t,
                                  ),
                                );
                              }}
                            >
                              Add Gems
                            </Button>

                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => {
                                setTicketsData((prev) =>
                                  prev.map((t) =>
                                    t.id === ticket.id
                                      ? {
                                          ...t,
                                          metadata: [
                                            {
                                              type: "token",
                                              metadata: {
                                                amount: "10000",
                                                reward_source: "jackpot",
                                                is_jackpot: true, // Keep for backward compatibility
                                                token_id: "imx",
                                              },
                                            },
                                          ],
                                        }
                                      : t,
                                  ),
                                );
                              }}
                            >
                              Add Jackpot
                            </Button>

                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => {
                                setTicketsData((prev) =>
                                  prev.map((t) =>
                                    t.id === ticket.id
                                      ? {
                                          ...t,
                                          metadata: [
                                            {
                                              type: "token",
                                              metadata: {
                                                amount: "500",
                                                reward_source: "referral",
                                                token_id: "imx",
                                              },
                                            },
                                          ],
                                        }
                                      : t,
                                  ),
                                );
                              }}
                            >
                              Add Referral
                            </Button>
                          </Stack>
                        )}
                    </Stack>
                  ))}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Stack direction="row" gap="base.spacing.x2">
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => {
                          // Add a ticket for current sweepstake
                          const newTicket: TicketData = {
                            status: "locked",
                            id: `ticket-${Date.now()}`,
                            type: "sweepstake_ticket",
                            granted_at: new Date().toISOString(),
                            expires_at: new Date(
                              Date.now() + 30 * 24 * 60 * 60 * 1000,
                            ).toISOString(),
                            source:
                              sweepstakeData.current?.trigger ||
                              "weekly-rewards-1",
                            rarity: "legendary",
                          };
                          setTicketsData((prev) => [...prev, newTicket]);
                          setTimeout(() => {
                            applyMockData();
                          }, 100);
                        }}
                      >
                        Add Current Ticket
                      </Button>

                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => {
                          // Add a ticket for previous sweepstake
                          const newTicket: TicketData = {
                            status: "claimable",
                            id: `ticket-${Date.now()}`,
                            type: "sweepstake_ticket",
                            granted_at: new Date(
                              Date.now() - 7 * 24 * 60 * 60 * 1000,
                            ).toISOString(),
                            expires_at: new Date(
                              Date.now() + 30 * 24 * 60 * 60 * 1000,
                            ).toISOString(),
                            source:
                              sweepstakeData.previous?.trigger ||
                              "weekly-rewards-0",
                            rarity: "epic",
                            metadata: [
                              {
                                type: "token",
                                metadata: {
                                  amount: "100",
                                  token_id: "imx",
                                },
                              },
                            ],
                          };
                          setTicketsData((prev) => [...prev, newTicket]);
                          setTimeout(() => {
                            applyMockData();
                          }, 100);
                        }}
                      >
                        Add Claimable Ticket
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              )}

              {activeTab === "gems" && (
                <Stack gap="base.spacing.x3">
                  <Heading size="xSmall">Wallet Gems Control</Heading>
                  <TextInput
                    type="number"
                    value={walletGemsData.gems}
                    onChange={(e) =>
                      updateWalletGems(Number.parseInt(e.target.value) || 0)
                    }
                    sizeVariant="medium"
                  >
                    <TextInput.Button>Gems Balance</TextInput.Button>
                  </TextInput>

                  <TextInput
                    type="number"
                    value={walletGemsData.dailyGemsClaimable || 0}
                    onChange={(e) => {
                      setWalletGemsData((prev) => ({
                        ...prev,
                        dailyGemsClaimable:
                          Number.parseInt(e.target.value) || 0,
                      }));
                    }}
                    sizeVariant="medium"
                  >
                    <TextInput.Button>Daily Claimable Gems</TextInput.Button>
                  </TextInput>
                </Stack>
              )}

              {activeTab === "errors" && (
                <Stack gap="base.spacing.x3">
                  <Heading size="xSmall">API Error Simulation</Heading>
                  <Stack gap="base.spacing.x2">
                    {Object.entries(apiErrors).map(([key, value]) => (
                      <Stack
                        key={key}
                        direction="row"
                        gap="base.spacing.x2"
                        sx={{ alignItems: "center" }}
                      >
                        <Checkbox
                          checked={value}
                          onChange={(e) => {
                            setApiErrors((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }));
                          }}
                        />
                        <Body size="small">Simulate error for {key} API</Body>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
